<template>
    <v-app>
        <header-card-public current_page="login"></header-card-public>
        <v-content class="bg-container">
            <v-layout fill-height row wrap>
               
                <!-- Left Side: Notifications (Desktop Only) -->
                <v-flex v-if="!isMobile" xs12 sm6 class="notifications-section">
                    <div class="notifications-bg">
                        <!-- <public-notifications></public-notifications> -->
                        <platform-showcase></platform-showcase>
                    </div>
                </v-flex>

                <!-- Right Side: Interactive Password Reset Section -->
                <v-flex xs12 sm6 :class="welcomeSectionClass">
                    <v-layout column fill-height align-center justify-center class="mobile-stack-container">
                        
                        <v-spacer v-if="isMobile"></v-spacer>

                        <div class="login-container">
                            <!-- Glassmorphic Login Card -->
                            <v-card :class="['mx-auto', isMobile ? 'welcome-card-mobile' : 'welcome-card']" flat>
                                <v-form @submit.prevent="executeRecaptcha" ref="formPasswordRecovery">
                                    <v-card-text class="px-0"> 
                                        <!-- Slate Blue Heading Target Layout Anchor -->
                                        <h3 class="text-xs-center welcome-message card-dark-text">Password Reset</h3>
                                        
                                        <v-alert v-if="error" :value="true" color="error" icon="error" class="rounded-4 mb-3" outline>
                                            {{ errorMessage }}
                                        </v-alert>          
                                        
                                        <v-layout row wrap> 
                                            <v-flex xs12 class="mt-2">
                                                <!-- Glass theme friendly input -->
                                                <v-text-field 
                                                    class="brand-input-field"
                                                    color="#1976D2"
                                                    label="Enter your registered email" 
                                                    v-model="email" 
                                                    :rules="[rules.required, rules.email]" 
                                                    :disabled="disabled"
                                                    @keyup="error=false"
                                                    >
                                                </v-text-field>  
                                            </v-flex>
                                        </v-layout> 
                                    </v-card-text>  
                                    
                                    <!-- Light separation divider line -->
                                    <div class="glass-divider my-2"></div>
                                    
                                    <v-card-actions class="px-0 pt-3">
                                        <v-spacer></v-spacer>
                                        <!-- Primary Action: Color locked to brand blue #1976D2 -->
                                        <v-btn color="#1976D2" type="submit" :loading="loading" class="no-uppercase rounded-4 primary-device-btn px-4 mr-2">Reset Password</v-btn>
                                        <!-- Secondary Action: Transparent matching outline layout -->
                                        <v-btn @click="$router.go(-1)" :disabled="disabled" class="no-uppercase rounded-4 secondary-password-btn px-4">Cancel</v-btn>
                                    </v-card-actions>
                                </v-form> 
                            </v-card>
                        </div>

                        <v-spacer v-if="isMobile"></v-spacer>

                        <!-- Responsive Footer structure extracted cleanly beneath column boundaries -->
                        <div class="global-desktop-footer caption">
                            <terms-privacy></terms-privacy>
                            <other-legal-docs></other-legal-docs>
                            <copyright></copyright>
                        </div>
                    </v-layout>
                </v-flex>
                
            </v-layout>
        </v-content>

        <v-dialog v-model="dialogOkOnly" persistent width="600">
            <ok-only :msg="dialogOkOnlyMsg" :dialogOkOnly.sync="dialogOkOnly" @ok="login()"></ok-only>
        </v-dialog>

        <recaptcha ref="recaptcha" @verify="submitReCaptcha"></recaptcha>
    </v-app>
</template>

<script>

import '@/assets/css/public-pages.css'
import userApi from '@/api/user.js'
import Recaptcha from '@/components/ReCaptcha.vue'
import publicPages from '@/helpers/public-pages.js'

export default {
    name: 'PasswordRecovery',
    metaInfo: {
        title: 'Password Recovery',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Enter registered email then click Continue. If successfull, temporary password will be sent to email.' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        Recaptcha,
        'platform-showcase': () => import('@/components/PublicNotifications/PlatformShowcase'),
        'header-card-public': () => import('@/components/Common/HeaderPublic'),
        'terms-privacy': () => import('@/components/Common/TermsAndPrivacy'),
        'other-legal-docs': () => import('@/components/Common/OtherLegalDocs'),
        'copyright': () => import('@/components/Common/Copyright'),
        'public-notifications': () => import('@/components/PublicNotifications/PublicNotifications'),
    },
    data () {
        return {
            dialog: false,
            disabled: false,
            company_name: '',
            loading: false,
            email: '',  
            firstName: '',
            lastName: '',
            error: false,  
            errorMessage: '',
            pass1: '',  //TODO validation for strong password
            pass2: '',
            show1: false,
            show2: false,
            title: 'Preliminary report',        
            drawer: false,
            dialogOkOnly: false,
            dialogOkOnlyMsg: "Password reset completed successfully. Please check your email for the temporary password. If the email doesn't appear in your inbox, kindly review your Spam folder.",
            rules: {
                required: value => !!value || 'Required.',
                min: v => v.length >= 8 || 'Min 8 characters',
                counter: value => value.length <= 20 || 'Max 20 characters',
                email: value => {
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(value) || 'Invalid e-mail.'
                }
            },
            recaptchaResponse: null,
        }
    },
    methods: {
        login() {
            this.dialogOkOnly = false
            this.$router.push('/signin-with-password')
        },
        submitReCaptcha(response) {
            this.recaptchaResponse = response
            this.resetPassword()
        },       
        executeRecaptcha () {
            // execute the recaptcha widget
            if (this.$refs.formPasswordRecovery.validate()) {
                this.loading = true
                this.disabled = true

                this.$refs.recaptcha.execute()
            }
        },
        clear() {
            this.$refs.formPasswordRecovery.reset()
            this.loading = false
            this.error = false
        },
        async resetPassword() {
            try{
                if (this.$refs.formPasswordRecovery.validate()) {
                    this.loading = true
                    const response = await userApi.resetPassword(this.email, this.recaptchaResponse)

                    if ((response) && response.status == 200) {
                        this.loading = false

                        if (response.data.success) {
                            this.dialogOkOnly = true
                        }else{
                            this.error = true
                            this.disabled = false
                            this.errorMessage = response.data.message
                        }
                    }
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
                this.loading = false
            }
        }
    },
    mounted() {
        this.$refs.formPasswordRecovery.reset()
    },
    computed: {
        notthesamepassword: function() {
            return this.pass1!=this.pass2
        },
        seasonDetails() {
            return publicPages.getSeasonDetails(this.$route.query.action, this.isMobile);
        },
        welcomeSectionClass() {
            return this.seasonDetails.className;
        },
        welcomeButtonColor() {
            return this.seasonDetails.buttonColor;
        },
    }
}
</script>